import _ from 'lodash';
import { MenuItem } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { Trans } from '@lingui/macro';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import {
    LDAP_ANONYMOUS_FIELD,
    LDAP_BIND_DN_FIELD,
    LDAP_DEFAULT_ROLE_FIELD,
    LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD,
    LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD,
    LDAP_OFFLINE_LOGIN_ENABLED_FIELD,
    LDAP_PORT_FIELD,
    LDAP_SECURE_FIELD,
    LDAP_SERVER_FIELD,
    LDAP_TLS_REJECT_UNAUTHORIZED_FIELD,
    LDAP_USER_SEARCH_BASE_FIELD,
    LDAP_USER_USERNAME_ATTR_FIELD,
    UNAUTHORIZED_OPTION_TEXT,
    UNAUTHORIZED_OPTION_VALUE,
} from './SsoAdminConstants';
import NgpTextField from '../../form/NgpTextField';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import NgpNumberField from '../../form/NgpNumberField';
import NgpRow from '../../form/NgpRow';
import NgpPasswordField from '../../form/NgpPasswordField';
import NgpSelect from '../../form/NgpSelect';
import ListFormField from '../../form/ListFormField';
import { getGroupAssociationColumns } from './SsoAdminHelper';
import GroupForm from './GroupForm';
import LdapVerify from './LdapVerify';

const groupAssociationColumns = getGroupAssociationColumns(true);

export default function Ldap({ formData, userRoleOptions, setDirty }) {
    const { watch } = useFormContext();
    const ldapSecure = watch(LDAP_SECURE_FIELD);
    const ldapAnonymous = watch(LDAP_ANONYMOUS_FIELD);

    return (
        <>
            <FormSection
                title={<Trans>Server Configuration</Trans>}
                subText={
                    <Trans>This section defines how nGeniusPULSE will connect to your Active Directory server</Trans>
                }
            >
                <NgpToggle
                    fieldName={LDAP_SECURE_FIELD}
                    label={<Trans>Secure</Trans>}
                    helpTipText={
                        <Trans>
                            Active Directory traffic secured via TLS 1.2. Disabling this uses basic authentication,
                            which sends unencrypted credentials.
                        </Trans>
                    }
                />

                {ldapSecure && (
                    <NgpToggle
                        fieldName={LDAP_TLS_REJECT_UNAUTHORIZED_FIELD}
                        label={<Trans>Validate Server Certificate</Trans>}
                        helpTipText={
                            <Trans>
                                Requires that the Active Directory server certificate has been signed by a valid CA. If
                                the Active Directory server has a self-signed certificate, this option should be
                                disabled.
                            </Trans>
                        }
                    />
                )}

                <NgpRow>
                    <NgpTextField
                        fieldName={LDAP_SERVER_FIELD}
                        label={<Trans>Server Hostname/IP</Trans>}
                        required
                        inputProps={{
                            size: 40,
                            maxLength: 4096,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                    />

                    <NgpNumberField
                        fieldName={LDAP_PORT_FIELD}
                        label={<Trans>Port</Trans>}
                        min="1"
                        max="65535"
                        inputProps={{
                            placeholder: ldapSecure ? '636' : '389',
                            size: 22,
                            maxLength: 5,
                        }}
                    />
                </NgpRow>

                <NgpToggle
                    fieldName={LDAP_ANONYMOUS_FIELD}
                    label={<Trans>Use Anonymous Binding</Trans>}
                    helpTipText={
                        <Trans>
                            Connects and binds to the Active Directory server without a username or password. This
                            configuration must be supported by the Active Directory server.
                        </Trans>
                    }
                />

                {!ldapAnonymous && (
                    <>
                        <NgpTextField
                            fieldName={LDAP_BIND_DN_FIELD}
                            label={<Trans>Bind DN</Trans>}
                            required
                            inputProps={{
                                size: 60,
                                maxLength: 4096,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                            helpTipText={
                                <Trans>
                                    The full distinguished name of the user to bind as on the Active Directory server.
                                    For example, username &quot;admin&quot; may have a Bind DN of
                                    &quot;CN=admin,BU=IT,OU=Corp,DC=mycompany,DC=com&quot;
                                </Trans>
                            }
                        />

                        <NgpPasswordField
                            fieldName={LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD}
                            label={<Trans>Bind Credentials</Trans>}
                            required
                            inputProps={{ size: 60, maxLength: 4096 }}
                            togglePasswordVisibility
                        />
                    </>
                )}
            </FormSection>

            <FormSection
                title={<Trans>User Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines how to link users between your Active Directory server and nGeniusPULSE
                    </Trans>
                }
            >
                <NgpToggle
                    fieldName={LDAP_OFFLINE_LOGIN_ENABLED_FIELD}
                    label={<Trans>Allow Offline Logins</Trans>}
                    helpTipText={
                        <Trans>
                            If the Active Directory server is unreachable, enabling this option allows users to log in
                            using the username and password they last successfully logged in with.
                        </Trans>
                    }
                />

                <NgpTextField
                    fieldName={LDAP_USER_SEARCH_BASE_FIELD}
                    label={<Trans>Search Base</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={
                        <Trans>
                            The location in the Active Directory tree to begin searching for users and groups. This is
                            typically the domain root. For example, &quot;company.com&quot; could have a search base of
                            &quot;DC=company,DC=com&quot;
                        </Trans>
                    }
                />

                <NgpTextField
                    fieldName={LDAP_USER_USERNAME_ATTR_FIELD}
                    label={<Trans>Username Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={
                        <Trans>
                            The Active Directory attribute which contains a user&apos;s username. Most configurations
                            will use &quot;sAMAccountName&quot; unless the Active Directory administrator has chosen to
                            use another attribute.
                        </Trans>
                    }
                />

                <NgpSelect fieldName={LDAP_DEFAULT_ROLE_FIELD} label={<Trans>Default User Role</Trans>}>
                    <MenuItem key={1} value={UNAUTHORIZED_OPTION_VALUE}>
                        {UNAUTHORIZED_OPTION_TEXT}
                    </MenuItem>
                    {_.map(userRoleOptions, (value, key) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </NgpSelect>
            </FormSection>

            <FormSection
                title={<Trans>Group Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines the relationship between the groups that users may belong to in Active
                        Directory and what role they will be given in nGeniusPULSE on first login
                    </Trans>
                }
                addSpacer
            >
                <ListFormField
                    fieldName={LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD}
                    dbSelected={_.get(formData, LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD)}
                    gridColumns={groupAssociationColumns}
                    setSelectionIsDirty={setDirty}
                    formComponent={<GroupForm isActiveDirectory />}
                />
            </FormSection>

            <LdapVerify formData={formData} />
        </>
    );
}
